import type { ChatMessage, CompletionRequest } from "./types";
import { normalizeApiBaseUrl } from "./settings";

type ChatCompletionsResponse = {
  choices?: Array<{
    message?: {
      content?: string;
    };
  }>;
  error?: {
    message?: string;
  };
};

export function buildMessages(request: CompletionRequest): ChatMessage[] {
  const contextText = request.context.selection.trim()
    ? request.context.selection
    : "No selection text was available from the active Office file.";

  return [
    {
      role: "system",
      content: request.settings.systemPrompt,
    },
    {
      role: "user",
      content: [
        `Office host: ${request.context.host}`,
        "Active selection or document context:",
        contextText,
        "User task:",
        request.instruction,
      ].join("\n\n"),
    },
  ];
}

export async function completeTask(request: CompletionRequest): Promise<string> {
  const apiBaseUrl = normalizeApiBaseUrl(request.settings.apiBaseUrl);
  if (!apiBaseUrl) {
    throw new Error("Configure an API base URL first.");
  }

  const apiKey = request.settings.apiKey.trim();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (apiKey) {
    headers.Authorization = `Bearer ${apiKey}`;
  }

  const response = await fetch(`${apiBaseUrl}/chat/completions`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      model: request.settings.model.trim(),
      messages: buildMessages(request),
      temperature: request.settings.temperature,
    }),
  });

  const payload = (await response.json().catch(() => ({}))) as ChatCompletionsResponse;
  if (!response.ok) {
    throw new Error(payload.error?.message ?? `API request failed with status ${response.status}.`);
  }

  const content = payload.choices?.[0]?.message?.content?.trim();
  if (!content) {
    throw new Error("The API returned an empty completion.");
  }

  return content;
}
